import { Roles } from "@agrigate/shared/lib/consts/roles";
import type { IActionableOrderSummary } from "@agrigate/shared/lib/services/NotificationService";
import ContentEditorLink from "@agrigate/shared-web-ui/lib/ContentEditorLink";
import {
    Box,
    Circle,
    Collapsible,
    Flex,
    Heading,
    Spacer,
    Text,
} from "@chakra-ui/react";
import { useAtom, useAtomValue } from "jotai";
import { compact } from "lodash";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import {
    FiArchive,
    FiAward,
    FiBarChart2,
    FiBookmark,
    FiBookOpen,
    FiCalendar,
    FiCheckSquare,
    FiChevronDown,
    FiClipboard,
    FiClock,
    FiCpu,
    FiDollarSign,
    FiDroplet,
    FiEdit3,
    FiFile,
    FiFileText,
    FiGift,
    FiGlobe,
    FiHash,
    FiInbox,
    FiKey,
    FiLayers,
    FiLayout,
    FiPackage,
    FiRefreshCw,
    FiRepeat,
    FiRss,
    FiSettings,
    FiShield,
    FiShoppingBag,
    FiShoppingCart,
    FiSliders,
    FiSmartphone,
    FiTag,
    FiThermometer,
    FiToggleRight,
    FiTool,
    FiTrendingUp,
    FiTruck,
    FiUmbrella,
    FiUsers,
} from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import BoxIcon from "../assets/icons/3d-box.svg?react";
import AnalyticsIcon from "../assets/icons/analytics-icon.svg?react";
import BagIcon from "../assets/icons/bag.svg?react";
import ClipTickIcon from "../assets/icons/clipboard-tick.svg?react";
import ClusterManagementIcon from "../assets/icons/cluster-management.svg?react";
import CCIcon from "../assets/icons/collection-center.svg?react";
import DashboardIcon from "../assets/icons/dashboard.svg?react";
import HandshakeIcon from "../assets/icons/handshake.svg?react";
import LeafIcon from "../assets/icons/leaf-icon.svg?react";
import LocationIcon from "../assets/icons/location.svg?react";
import MirrorIcon from "../assets/icons/mirror.svg?react";
import OrderApproval from "../assets/icons/order_approve.svg?react";
import RollIcon from "../assets/icons/roll.svg?react";
import ShopIcon from "../assets/icons/shop.svg?react";
import UserBoxIcon from "../assets/icons/user-box.svg?react";
import UserGroupIcon from "../assets/icons/user-group.svg?react";
import ZoomChartIcon from "../assets/icons/zoom-chart.svg?react";
import { useActionableOrders } from "../stores/actionable-orders";
import type { NavGroup } from "../stores/navItems";
import { collapsedSectionsAtom } from "../stores/navItems";
import { sessionAtom } from "../stores/session";
import { isAppScopeContentManager } from "../utils/app-scope";
import { getTrackingId, trackEvent } from "../utils/posthog";

export const getNavGroups = (opts: {
    isContentMgmtEnabled: boolean;
    actionableOrders: IActionableOrderSummary;
    roles: string[];
}): NavGroup[] => {
    const contentSection = {
        key: "appContent",
        header: "App Content",
        icon: <FiLayout />,
        items: [
            {
                path: "/app-content/hub?tab=feeds",
                label: "Farmer Feed",
                icon: <FiRss size="18px" />,
            },
            {
                path: "/app-content/hub?tab=forms",
                label: "Surveys",
                icon: <FiCheckSquare size="18px" />,
            },
            {
                path: "/translations",
                label: "Translations",
                icon: <FiGlobe size="18px" />,
            },
            {
                path: "/app-content/content-sync",
                label: "Content Sync",
                icon: <FiRefreshCw size="18px" />,
            },
            {
                path: "/content-management",
                label: "Content Management",
                icon: <FiEdit3 size="18px" />,
            },
        ],
    };
    if (isAppScopeContentManager()) {
        return [contentSection];
    }
    return compact([
        {
            key: "primary",
            items: [
                {
                    path: "/",
                    label: "Dashboard",
                    icon: <DashboardIcon />,
                },
            ],
        },
        {
            key: "analytics",
            header: "Analytics",
            icon: <FiBarChart2 />,
            items: compact([
                NEW_ANALYTICS_PAGE_ENABLED === "true"
                    ? {
                          path: "/order-analytics/new-dashboard",
                          label: "New Order Analytics",
                          icon: <AnalyticsIcon />,
                      }
                    : null,
                {
                    path: "/order-analytics",
                    label: "Order Analytics",
                    icon: <FiTrendingUp size="18px" />,
                },
                {
                    path: "/app-visitors",
                    label: "App Analytics",
                    icon: <FiSmartphone size="18px" />,
                },
                {
                    path: "/investor-report",
                    label: "Investor Report Generator",
                    icon: <FiFileText size="18px" />,
                },
            ]),
        },
        opts.roles.includes(Roles.ORDER_VIEWER)
            ? {
                  key: "orderLifecycle",
                  header: "Commodity Transactions",
                  icon: <FiRepeat />,
                  items: [
                      {
                          path: "/procurement/list",
                          label: "Procurement Orders",
                          icon: <FiShoppingCart size="18px" />,
                          pendingCount:
                              opts.actionableOrders.collectionTargets?.length ??
                              "",
                      },
                      {
                          label: "Farmer Lots",
                          path: "/seller-orders/list",
                          icon: <ShopIcon />,
                      },
                      {
                          path: "/buyer-orders/list",
                          label: "Buyer Orders",
                          icon: <BagIcon />,
                          pendingCount:
                              opts.actionableOrders.buyerOrders?.length ?? "",
                      },
                      {
                          path: "/market-insights/list",
                          label: "Market Insights",
                          icon: <ZoomChartIcon />,
                      },
                      {
                          path: "/shipments/list",
                          label: "Shipments",
                          icon: <BoxIcon />,
                          pendingCount:
                              opts.actionableOrders.shipments?.length ?? "",
                      },
                      {
                          path: "/transactions",
                          label: "Payments",
                          icon: <RollIcon />,
                      },
                      {
                          path: "/cp-invoices",
                          label: "CP Invoices",
                          icon: <FiFile size="18px" />,
                      },
                      ...compact([
                          opts.roles.includes(Roles.PAYMENT_COORDINATOR)
                              ? {
                                    path: "/approvals/list",
                                    label: "Payouts & Approvals",
                                    icon: <OrderApproval />,
                                }
                              : null,
                      ]),
                      {
                          path: "/produces-and-categories/hub",
                          label: "Produces & Categories",
                          icon: <LeafIcon />,
                      },
                  ],
              }
            : null,
        {
            key: "soilTesting",
            header: "Soil Testing",
            icon: <FiThermometer />,
            items: [
                {
                    path: "/soil-testing/tests",
                    label: "Tests",
                    icon: <FiDroplet size="18px" />,
                },
                {
                    path: "/soil-testing/bookings",
                    label: "Bookings",
                    icon: <FiClipboard size="18px" />,
                },
            ],
        },
        opts.roles.includes(Roles.REWARD_EDITOR)
            ? {
                  key: "rewards",
                  header: "Rewards",
                  icon: <FiAward />,
                  items: compact([
                      {
                          path: "/app-content/hub?tab=rewards",
                          label: "Configuration",
                          icon: <FiSliders size="18px" />,
                      },
                      {
                          path: "/rewards/redemptions",
                          label: "Earnings/Redemptions",
                          icon: <FiGift size="18px" />,
                      },
                      opts.roles.includes(Roles.LOCATION_EDITOR)
                          ? {
                                path: "/rewards/feature-availability",
                                label: "Feature Availability",
                                icon: <FiToggleRight size="18px" />,
                            }
                          : null,
                  ]),
              }
            : null,
        {
            key: "marketplace",
            header: "Marketplace",
            icon: <FiTag />,
            items: [
                {
                    path: "/marketplace/orders",
                    label: "Orders",
                    icon: <FiShoppingBag size="18px" />,
                },
                {
                    path: "/marketplace/pre-booking",
                    label: "Pre-booking",
                    icon: <FiClock size="18px" />,
                },
                {
                    path: "/marketplace/catalog/products",
                    label: "Products",
                    icon: <FiPackage size="18px" />,
                },
                {
                    path: "/marketplace/catalog/brands",
                    label: "Catalog",
                    icon: <FiBookOpen size="18px" />,
                },
            ],
        },
        {
            key: "equipment-rental",
            header: "Equipment Rental",
            icon: <FiTool />,
            items: [
                {
                    path: "/equipment-rental/bookings",
                    label: "Bookings",
                    icon: <FiBookmark size="18px" />,
                },
                {
                    path: "/equipment-rental/scheduler",
                    label: "Scheduler",
                    icon: <FiCalendar size="18px" />,
                },
                {
                    path: "/equipment-rental/categories",
                    label: "Equipments",
                    icon: <FiTruck size="18px" />,
                },
            ],
        },
        {
            key: "inventory",
            header: "Inventory Management",
            icon: <FiArchive />,
            items: [
                {
                    path: "/inventory/list",
                    label: "Inventory",
                    icon: <ClipTickIcon />,
                },
                {
                    path: "/collection-center",
                    label: "Collection Center",
                    icon: <CCIcon />,
                },
                {
                    path: "/cluster-management/list",
                    label: "Cluster Management",
                    icon: <ClusterManagementIcon />,
                },
            ],
        },
        {
            key: "insurance",
            header: "Insurance",
            icon: <FiUmbrella />,
            items: [
                {
                    path: "/insurance/providers",
                    label: "Providers",
                    icon: <FiShield size="18px" />,
                },
                {
                    path: "/insurance/applications",
                    label: "Applications",
                    icon: <FiInbox size="18px" />,
                },
                {
                    path: "/insurance/plans",
                    label: "Plans",
                    icon: <FiLayers size="18px" />,
                },
            ],
        },
        {
            header: "User Management",
            icon: <FiUsers />,
            key: "userMgmt",
            items: compact([
                opts.roles.includes(Roles.PROFILE_VIEWER)
                    ? {
                          label: "Users",
                          path: "/users",
                          icon: <UserGroupIcon />,
                      }
                    : null,
                opts.roles.includes(Roles.PAYMENT_COORDINATOR)
                    ? {
                          path: "/cp-commissions/list",
                          label: "Channel Partner Commissions",
                          icon: <UserBoxIcon />,
                      }
                    : null,
            ]),
        },
        {
            key: "utils",
            header: "Utilities",
            icon: <FiSettings />,
            items: compact([
                {
                    path: "/field-tracking",
                    label: "Field Location Tracking",
                    icon: <LocationIcon />,
                    roles: ["FIELD_SUPERVISOR"],
                },
                opts.roles.includes(Roles.ORDER_EDITOR)
                    ? {
                          path: "/data-upload",
                          label: "Reconcile/Upload Data",
                          icon: <MirrorIcon />,
                      }
                    : null,
                {
                    path: "/poc/list",
                    label: "POC Management",
                    icon: <HandshakeIcon />,
                },
                opts.roles.includes(Roles.ORDER_VIEWER)
                    ? {
                          path: "/pnl/finances",
                          label: "PNL/Finances",
                          icon: <FiDollarSign size="18px" />,
                      }
                    : null,
                opts.roles.includes(Roles.DEV) ||
                opts.roles.includes(Roles.ADMIN)
                    ? {
                          path: "/background-tasks/overview",
                          label: "Background jobs",
                          icon: <FiCpu size="18px" />,
                      }
                    : null,
                opts.roles.includes(Roles.ADMIN)
                    ? {
                          path: "/access-requests/list",
                          label: "Access Requests",
                          icon: <FiKey size="18px" />,
                      }
                    : null,
                opts.roles.includes(Roles.ADMIN)
                    ? {
                          path: "/sequences",
                          label: "Sequence Management",
                          icon: <FiHash size="18px" />,
                      }
                    : null,
            ]),
        },
        /* opts.isContentMgmtEnabled ? */ contentSection /*  : {
        items: [{
            path: "https://cm.staging.agrigateinternal.net",
            label: "App Content",
        }],
    } */,
    ]);
};

export default function DashboardNav(p: {
    isCompact?: boolean;
    closeMobileModal?: () => void;
}): ReactElement {
    const isContentMgmtEnabled = CONTENT_MGMT_ENABLED !== "false";
    const loc = useLocation();
    const session = useAtomValue(sessionAtom);
    const [collapsedSections, setCollapsedSections] = useAtom(
        collapsedSectionsAtom,
    );
    const [activePath, setActivePath] = useState<string>("/");

    const actionableOrdersH = useActionableOrders();

    useEffect(() => {
        actionableOrdersH.refresh();
    }, []);

    useEffect(() => {
        getTrackingId(true);
    }, []);

    const navGroups = useMemo(
        () =>
            getNavGroups({
                isContentMgmtEnabled,
                actionableOrders: actionableOrdersH.orders,
                roles: session?.roles ?? [],
            }),
        [isContentMgmtEnabled, actionableOrdersH.orders, session?.roles],
    );

    useEffect(() => {
        console.log("Location changed::", loc.pathname);
        trackEvent("PageNavigation", loc.pathname);
    }, [loc]);

    // Track active path based on nav item matching
    useEffect(
        function syncActivePath() {
            const currentPath = loc.pathname.split("?")[0];

            // Find the best matching nav item
            let bestMatch: string | null = null;
            let bestMatchLength = 0;
            let bestMatchGroupKey: string | null = null;

            for (const group of navGroups) {
                for (const item of group.items ?? []) {
                    const itemPath = item.path.split("?")[0];

                    // Exact match takes priority
                    if (itemPath === currentPath) {
                        setActivePath(item.path);
                        // Expand the section containing this item, collapse others
                        setCollapsedSections(
                            new Set(
                                navGroups
                                    .map(g => g.key)
                                    .filter(key => key !== group.key),
                            ),
                        );
                        return;
                    }

                    // For non-exact matches, prefer longer/more specific paths
                    // Only match if current path starts with the item path
                    if (
                        currentPath.startsWith(`${itemPath}/`) ||
                        (itemPath !== "/" && currentPath === itemPath)
                    ) {
                        if (itemPath.length > bestMatchLength) {
                            bestMatch = item.path;
                            bestMatchLength = itemPath.length;
                            bestMatchGroupKey = group.key;
                        }
                    }
                }
            }

            // Update active path if a match was found, otherwise keep the old value
            if (bestMatch) {
                setActivePath(bestMatch);
                // Expand the section containing the best match, collapse others
                if (bestMatchGroupKey) {
                    setCollapsedSections(
                        new Set(
                            navGroups
                                .map(g => g.key)
                                .filter(key => key !== bestMatchGroupKey),
                        ),
                    );
                }
            }
        },
        [loc.pathname, navGroups],
    );

    const toggleSection = (key: string) => {
        setCollapsedSections((prev: Set<string>) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    return (
        <Flex direction="column" alignItems="stretch">
            {navGroups.map((navGroup, idx) => {
                if (!navGroup.items?.length) return null;
                const isCollapsed = collapsedSections.has(navGroup.key);
                const hasHeader = !!navGroup.header;
                return (
                    <Box
                        key={navGroup.key}
                        px="20px"
                        borderTopWidth="1px"
                        borderTopStyle="solid"
                        borderTopColor={
                            idx > 0 ? "neutralGray.100" : "transparent"
                        }
                    >
                        {hasHeader && (
                            <Collapsible.Root open={!isCollapsed}>
                                <Collapsible.Trigger asChild>
                                    <Heading
                                        fontSize="xs"
                                        color={
                                            isCollapsed ? "black" : "gray.400"
                                        }
                                        py="10px"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        display="flex"
                                        alignItems="center"
                                        cursor="pointer"
                                        userSelect="none"
                                        onClick={() =>
                                            toggleSection(navGroup.key)
                                        }
                                        _hover={{ color: "gray.500" }}
                                    >
                                        {navGroup.icon && (
                                            <Flex
                                                as="span"
                                                mr={p.isCompact ? "0" : "8px"}
                                                fontSize={
                                                    p.isCompact
                                                        ? "18px"
                                                        : "14px"
                                                }
                                                alignItems="center"
                                                color={
                                                    isCollapsed
                                                        ? "black"
                                                        : "gray.400"
                                                }
                                            >
                                                {navGroup.icon}
                                            </Flex>
                                        )}
                                        {!p.isCompact && (
                                            <>
                                                {navGroup.header}
                                                <Spacer />
                                                {navGroup.header ===
                                                    "App Content" &&
                                                    !isAppScopeContentManager() && (
                                                        <ContentEditorLink
                                                            contentMgmtUrl={
                                                                CONTENT_MGMT_URL
                                                            }
                                                        />
                                                    )}
                                                <Collapsible.Indicator
                                                    asChild
                                                    transition="transform 0.2s"
                                                    _open={{
                                                        transform:
                                                            "rotate(180deg)",
                                                    }}
                                                >
                                                    <FiChevronDown />
                                                </Collapsible.Indicator>
                                            </>
                                        )}
                                    </Heading>
                                </Collapsible.Trigger>
                                <Collapsible.Content overflow="visible">
                                    {navGroup.items?.map(item => {
                                        const isActive =
                                            item.path === activePath;
                                        return (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                style={{ display: "block" }}
                                            >
                                                <Box
                                                    py="10px"
                                                    position="relative"
                                                    whiteSpace="nowrap"
                                                    overflow="visible"
                                                    onClick={p.closeMobileModal}
                                                >
                                                    {isActive && (
                                                        <Box
                                                            position="absolute"
                                                            left="-10px"
                                                            right="-10px"
                                                            top="0"
                                                            bottom="0"
                                                            backgroundColor={
                                                                isActive
                                                                    ? "gray.100"
                                                                    : "transparent"
                                                            }
                                                            zIndex="1"
                                                            borderRadius="4px"
                                                        />
                                                    )}
                                                    <Flex
                                                        zIndex="2"
                                                        direction="row"
                                                        gap={2}
                                                        alignItems="center"
                                                        fontSize="xs"
                                                        position="relative"
                                                        whiteSpace="nowrap"
                                                        overflow="hidden"
                                                    >
                                                        {item.icon}
                                                        {!p.isCompact && (
                                                            <Text
                                                                fontSize="xs"
                                                                as="span"
                                                            >
                                                                {item.label}
                                                            </Text>
                                                        )}
                                                        {item?.pendingCount ? (
                                                            p.isCompact ? (
                                                                <Circle
                                                                    bg={
                                                                        "red.500"
                                                                    }
                                                                    position={
                                                                        "absolute"
                                                                    }
                                                                    p={1}
                                                                    right={3}
                                                                    top={0}
                                                                />
                                                            ) : (
                                                                <Circle
                                                                    ml={"auto"}
                                                                    mr={2}
                                                                    bg={
                                                                        "red.500"
                                                                    }
                                                                    size={"7"}
                                                                >
                                                                    <Text
                                                                        color={
                                                                            "white"
                                                                        }
                                                                        fontSize={
                                                                            "xs"
                                                                        }
                                                                        p={1}
                                                                        fontWeight={
                                                                            "bold"
                                                                        }
                                                                    >
                                                                        {
                                                                            item.pendingCount
                                                                        }
                                                                    </Text>
                                                                </Circle>
                                                            )
                                                        ) : null}
                                                    </Flex>
                                                </Box>
                                            </Link>
                                        );
                                    })}
                                </Collapsible.Content>
                            </Collapsible.Root>
                        )}
                        {!hasHeader &&
                            navGroup.items?.map(item => {
                                const isActive = item.path === activePath;
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        style={{ display: "block" }}
                                    >
                                        <Box
                                            py="10px"
                                            position="relative"
                                            whiteSpace="nowrap"
                                            overflow="visible"
                                            onClick={p.closeMobileModal}
                                        >
                                            {isActive && (
                                                <Box
                                                    position="absolute"
                                                    left="-5px"
                                                    right="-5px"
                                                    top="0"
                                                    bottom="0"
                                                    backgroundColor={
                                                        isActive
                                                            ? "gray.100"
                                                            : "transparent"
                                                    }
                                                    zIndex="1"
                                                    borderRadius="4px"
                                                />
                                            )}
                                            <Flex
                                                zIndex="2"
                                                direction="row"
                                                gap={2}
                                                alignItems="center"
                                                fontSize="xs"
                                                position="relative"
                                                whiteSpace="nowrap"
                                                overflow="hidden"
                                            >
                                                {item.icon}
                                                {!p.isCompact && (
                                                    <Text
                                                        fontSize="xs"
                                                        as="span"
                                                    >
                                                        {item.label}
                                                    </Text>
                                                )}
                                                {item?.pendingCount ? (
                                                    p.isCompact ? (
                                                        <Circle
                                                            bg={"red.500"}
                                                            position={
                                                                "absolute"
                                                            }
                                                            p={1}
                                                            right={3}
                                                            top={0}
                                                        />
                                                    ) : (
                                                        <Circle
                                                            ml={"auto"}
                                                            mr={2}
                                                            bg={"red.500"}
                                                            size={"7"}
                                                        >
                                                            <Text
                                                                color={"white"}
                                                                fontSize={"xs"}
                                                                p={1}
                                                                fontWeight={
                                                                    "bold"
                                                                }
                                                            >
                                                                {
                                                                    item.pendingCount
                                                                }
                                                            </Text>
                                                        </Circle>
                                                    )
                                                ) : null}
                                            </Flex>
                                        </Box>
                                    </Link>
                                );
                            })}
                    </Box>
                );
            })}
        </Flex>
    );
}
